'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */

import classNames from 'classnames';
import { FC } from 'react';
import { AlignmentClasses, TextClasses } from '../../types';
import { CtaButtonComponents } from './CtaButtonComponents';
import { RewardContent } from './RewardContent';
import {
  CmsOffer,
  ComponentCtaButton,
  ComponentFlexibleHeroBlock,
  EnumFlexibleHeroBlockRewardType,
  EnumFlexibleHeroBlockTheme,
} from '$/(cms)/_modules/cmsService/types';

type CampaignLayoutProps = {
  alignment: AlignmentClasses;
  buttons: ComponentCtaButton[];
  bodyCopy?: string;
  headline?: string;
  headingId?: string;
  isPageHeadline?: boolean;
  imageDesktop: ComponentFlexibleHeroBlock['imageDesktop'];
  imageMobile: ComponentFlexibleHeroBlock['imageMobile'];
  lang: string;
  offers?: CmsOffer[];
  overline?: string;
  rewardType?: EnumFlexibleHeroBlockRewardType;
  textClasses: TextClasses;
  theme?: EnumFlexibleHeroBlockTheme;
};

export const CampaignLayout: FC<CampaignLayoutProps> = ({
  alignment,
  buttons,
  bodyCopy,
  headline,
  headingId,
  isPageHeadline = true,
  imageDesktop,
  imageMobile,
  lang,
  offers,
  overline,
  rewardType,
  textClasses,
  theme,
}) => {
  const HeadingTag = isPageHeadline ? 'h1' : 'div';
  const rewardContent = RewardContent({
    rewardType,
    offers,
    imageDesktop,
    imageMobile,
    lang,
    headline,
    theme,
  });
  const showReward = Boolean(rewardContent);

  return (
    <div data-component="flexible-hero-block-campaign-layout">
      <div className={classNames(alignment.container)}>
        {(!!headline || !!overline) && (
          <div className="flex flex-col gap-1">
            {overline && (
              <div
                className={classNames(
                  'mhg-alpha-typography-title-1',
                  textClasses.campaignOverline,
                )}
              >
                {overline}
              </div>
            )}
            {headline && (
              // The Flexible Hero block replaces the page's default PageHeader
              // (see PageContent `shouldRenderDefaultHero`), so its headline
              // must carry the page's single <h1> for SEO. Only the
              // page-level hero renders <h1>; stacked heroes fall back to <div>.
              // Typography matches ThePageHeader's <h1>.
              <HeadingTag
                id={headingId}
                className={classNames(
                  'mhg-alpha-typography-display-3',
                  textClasses.headline,
                )}
              >
                {headline}
              </HeadingTag>
            )}
          </div>
        )}

        {bodyCopy && (
          <div
            className={classNames(
              'mhg-alpha-typography-body-1-relaxed md:max-w-[--general-6-col-width]',
              textClasses.body,
            )}
          >
            {bodyCopy}
          </div>
        )}

        {buttons.length > 0 && (
          <div className={alignment.ctaWrapper}>
            <CtaButtonComponents buttons={buttons} />
          </div>
        )}

        {showReward && <div className="w-full">{rewardContent}</div>}
      </div>
    </div>
  );
};
